import { Box, Chip, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import Autocomplete from 'components/autocomplete';
import Checkbox from 'components/checkbox';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { createFuzzyFilterOptions } from 'utils/fuzzy-filter';
import { normalizeName } from 'utils/list-sort';
import { useSitesState } from 'views/order-feed/context/sites-context';
import { Site } from 'views/order-feed/types/site.type';

type SiteAutocompleteRoute = {
  /** Fallback route for a selected site and the route used when clearing. */
  basePath: string;
  /** Optional replacement for basePath when clearing, preserving route-specific structure. */
  clearPath?: string;
  /** Optional site-specific destination resolver, such as OFD 2.0 routing. */
  getPath?: (site: Site) => string;
};

type CommonProps = {
  route: SiteAutocompleteRoute;
};

type SingleSiteAutocompleteProps = CommonProps & {
  multiple?: false;
  value: Site | null;
};

type MultipleSiteAutocompleteProps = CommonProps & {
  multiple: true;
  values: Site[];
  onChange: (sites: Site[]) => void;
};

type SiteAutocompleteProps = SingleSiteAutocompleteProps | MultipleSiteAutocompleteProps;

type SiteListboxContextValue = {
  allSitesSelected: boolean;
  disabled: boolean;
  onSelectAll: () => void;
  selectAllLabel: string;
};

const SiteListboxContext = React.createContext<SiteListboxContextValue | null>(null);

const SiteListbox = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>((props, ref) => {
  const context = React.useContext(SiteListboxContext);
  const { children, ...listboxProps } = props;

  return (
    <ul
      {...listboxProps}
      ref={ref}>
      {context && (
        <Box
          component="li"
          display="flex"
          alignItems="center"
          gap={1}
          onClick={context.disabled ? undefined : context.onSelectAll}
          sx={(theme) => ({
            cursor: context.disabled ? 'default' : 'pointer',
            padding: theme.spacing(1.5, 4),
            '& .MuiFormControl-root': {
              display: 'flex',
            },
            '& .MuiFormControlLabel-root': {
              margin: 0,
            },
            '& .MuiButtonBase-root': {
              padding: 0,
            },
          })}>
          <Checkbox
            dark
            checked={context.allSitesSelected}
            disabled={context.disabled}
            onClick={(event) => event.stopPropagation()}
            onChange={context.onSelectAll}
          />
          <Typography color="darkBlue.main">{context.selectAllLabel}</Typography>
        </Box>
      )}
      {children}
    </ul>
  );
});

SiteListbox.displayName = 'SiteListbox';

const SiteChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'transparent',
  border: `1px solid ${theme.palette.common.white}`,
  color: theme.palette.common.white,
  '& .MuiChip-deleteIcon': {
    color: theme.palette.common.white,
  },
  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const getSiteLabel = (site: Site) => site.name;

const getSiteOptions = (sites: Site[] | undefined) =>
  [...(sites ?? [])].sort(
    (first, second) =>
      Number(second.hasRecentOrders) - Number(first.hasRecentOrders) ||
      normalizeName(first.name).localeCompare(normalizeName(second.name))
  );

const SiteAutocomplete: React.FC<SiteAutocompleteProps> = (props) => {
  const intl = useIntl();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    sitesQuery: { data: sites, isLoading },
  } = useSitesState();
  const options = getSiteOptions(sites);
  const route = props.route;
  const label = intl.formatMessage({
    id: props.multiple ? 'orderFeed.listOrderSupplyPlan.filter.sitesLabel' : 'orderFeed.listOrderSupplyPlan.filter.siteLabel',
  });
  const noOptionsText = intl.formatMessage({ id: 'general.noResults' });
  const getPath = (site: Site) => route.getPath?.(site) || `${route.basePath}/${site.siteErpId}/${site.globalSiteId}`;
  const goToBasePath = () => history.replace(route.clearPath || route.basePath);
  const getSiteGroup = (site: Site) =>
    site.hasRecentOrders
      ? intl.formatMessage({ id: 'orderFeed.listOrderSupplyPlan.siteList.active' })
      : intl.formatMessage({ id: 'orderFeed.listOrderSupplyPlan.siteList.inactive' });
  // Hold multi-select changes locally so parent consumers commit and refetch once when the popover closes.
  const [isMultipleOpen, setIsMultipleOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState<Site[]>(props.multiple ? props.values : []);

  const handleSingleChange = (site: Site | null) => {
    if (props.multiple) return;
    if (site) history.push(getPath(site));
    if (!site) goToBasePath();
  };

  const handleMultipleChange = (values: Site[]) => {
    if (!props.multiple) return;

    if (!isMultipleOpen) {
      props.onChange(values);
      if (!values.length) goToBasePath();
      return;
    }

    setPendingValues(values);
  };

  if (props.multiple) {
    const values = isMultipleOpen ? pendingValues : props.values;
    const allSitesSelected = options.length > 0 && values.length === options.length;

    return (
      <SiteListboxContext.Provider
        value={{
          allSitesSelected,
          disabled: !options.length || isLoading,
          onSelectAll: () => handleMultipleChange(allSitesSelected ? [] : options),
          selectAllLabel: intl.formatMessage({ id: 'general.selectAll' }),
        }}>
        <Autocomplete<Site, true>
          sx={{ width: '100%', maxWidth: { xs: '100%', sm: 320 } }}
          ListboxComponent={SiteListbox}
          limitTags={1}
          fullWidth={isMobile}
          label={label}
          loading={isLoading}
          disabled={!isLoading && !options.length}
          options={options}
          getOptionKey={(site) => site.globalSiteId}
          multiple
          value={values}
          onOpen={() => {
            setPendingValues(props.values);
            setIsMultipleOpen(true);
          }}
          openOnFocus
          noOptionsText={noOptionsText}
          getOptionLabel={getSiteLabel}
          filterOptions={createFuzzyFilterOptions((site) => site.name)}
          groupBy={getSiteGroup}
          renderOption={({ key, ...optionProps }, site) => (
            <Box key={key} component="li" {...optionProps} display="flex" alignItems="center" gap={1} color={theme.palette.darkBlue.main}>
              {site.name}
            </Box>
          )}
          renderValue={(tagValues, getItemProps) =>
            tagValues.length > 0 && (
              <>
                <SiteChip
                  {...getItemProps({ index: 0 })}
                  key={tagValues[0].globalSiteId}
                  label={getSiteLabel(tagValues[0])}
                />
                {tagValues.length > 1 && <Typography color="common.white">+{tagValues.length - 1}</Typography>}
              </>
            )
          }
          isOptionEqualToValue={(option, selected) => option.globalSiteId === selected.globalSiteId}
          onChange={(_, selected) => handleMultipleChange(selected)}
          onClose={() => {
            setIsMultipleOpen(false);
            props.onChange(pendingValues);

            if (pendingValues.length === 1) history.replace(getPath(pendingValues[0]));
            else goToBasePath();
          }}
        />
      </SiteListboxContext.Provider>
    );
  }

  return (
    <Autocomplete<Site>
      fullWidth={isMobile}
      label={label}
      loading={isLoading}
      disabled={!isLoading && !options.length}
      options={options}
      getOptionKey={(site) => site.globalSiteId}
      value={props.value}
      openOnFocus
      disableCloseOnSelect={false}
      noOptionsText={noOptionsText}
      getOptionLabel={getSiteLabel}
      filterOptions={createFuzzyFilterOptions((site) => site.name)}
      groupBy={getSiteGroup}
      renderOption={({ key, ...optionProps }, site) => (
        <Box key={key} component="li" {...optionProps} display="flex" alignItems="center" gap={1}>
          {site.name}
        </Box>
      )}
      isOptionEqualToValue={(option, selected) => option.globalSiteId === selected.globalSiteId}
      onChange={(_, site) => handleSingleChange(site)}
    />
  );
};

export { SiteAutocomplete };
export type { SiteAutocompleteRoute };
